import { escapeBelowSpotlight, findLive } from '@forecastify/ui';
import type { TourAction, TourStep } from './tour-steps';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ F-2 · the tour's hands: bring the app to a step, from wherever it is.      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `Tour` decides WHICH step; this decides nothing but how to get there. One method does the work —
 * `enter(step)` — and it is the same for every door: Next, Previous, Skip this section, a step
 * skipped on the way. Each step names its page and its clicks (`tour-steps.ts`), so arriving never
 * depends on where the tour was before.
 *
 * ⚠️⚠️ **ONE TRANSITION AT A TIME, AND A NEW ONE CANCELS THE OLD ONE AT ONCE.** Every `enter` takes
 * a token and cancels the one before it; every wait checks it, so a superseded transition stops
 * clicking within one tick. The new transition then waits for the old one to settle before it
 * touches anything — two of them interleaving clicks and Escapes is how a rapid Next, Next,
 * Previous used to end on one step with another step's form open over it.
 *
 * ⚠️⚠️ **WHAT THE TOUR OPENS, IT CLOSES — ITSELF, AND BY ELEMENT.** A click marked `opens` records the
 * modal surface that appeared after it. Entering a step closes, top-down, every recorded surface the
 * step does not open too, and a step that opens the same ones in the same order finds them already
 * open and skips the clicks that would open them again. Closing is an Escape the spotlight lets pass
 * (`escapeBelowSpotlight`), sent only while that recorded element is still live, and finished only
 * when it is gone or playing its exit (`inert`) — never a fixed pause, and never an Escape with
 * nothing of ours open, which used to reach the spotlight and close the tour.
 */

export interface TourTiming {
  /** A `required` click: a row that has to load first. */
  readonly required: number;
  /** Any other click: it is on the screen already, or it is not coming. */
  readonly optional: number;
  /** A page: until the address is the step's. */
  readonly route: number;
  /** Then until the page's own screen has mounted (`data-screen`). */
  readonly screen: number;
  /** A surface appearing after the click that opens it, or going after its Escape. */
  readonly surface: number;
  /** How often everything above is looked at. */
  readonly tick: number;
}

export const TOUR_TIMING: TourTiming = {
  required: 2500,
  optional: 1000,
  route: 2500,
  screen: 800,
  surface: 1500,
  tick: 40,
};

/** `ready`: point at the target. `missing`: a required element never came — skip the step. */
export type TourOutcome = 'ready' | 'missing' | 'cancelled';

interface Token {
  cancelled: boolean;
}

interface Opened {
  readonly key: string;
  readonly element: HTMLElement;
}

const MODAL = '[role="dialog"][aria-modal="true"]';
/** What closes on an outside press rather than on the tour's Escape: menus, listboxes, popovers. */
const DISMISSABLE = '[role="menu"], [role="listbox"], [role="dialog"]:not([aria-modal="true"])';

/**
 * Whether a newer transition has taken over. ⚠️ A call, not a property read: the flag changes across
 * an `await`, and TypeScript, which cannot see that, would narrow a second read to the first.
 */
const stopped = (token: Token): boolean => token.cancelled;

/**
 * ⚠️⚠️ **WHETHER THE PAGE IS STILL THERE.** A transition is a chain of awaits, and the tour can be
 * unmounted in the middle of one — the workspace changed, or, in a test, the test ended. Its
 * `finish()` is fire-and-forget on unmount, so the chain wakes from a pause into whatever is left.
 * In CI that was a torn-down jsdom with no `window` and no `document`, and the read threw as an
 * UNHANDLED REJECTION after the run — every test green, the job red, at random (Oct 2026). Every
 * loop asks this before it touches the page, and stops quietly when the answer is no.
 */
const pageGone = (): boolean => typeof document === 'undefined' || typeof window === 'undefined';

/* ⚠️ `globalThis`, not `window`: a pause must still resolve after the page has gone, so the chain
   reaches its next `pageGone()` check and ends instead of throwing. */
const pause = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    globalThis.setTimeout(resolve, ms);
  });

/** On screen, not the spotlight's own, and not playing its exit. */
const live = (element: Element): boolean =>
  element.isConnected &&
  element.closest('[data-spotlight]') === null &&
  element.closest('[inert]') === null;

const liveModals = (): HTMLElement[] =>
  [...document.querySelectorAll<HTMLElement>(MODAL)].filter(live);

/** Every `data-screen` on the page, and which screen each says it is. */
const screens = (): Map<Element, string> =>
  new Map(
    [...document.querySelectorAll('[data-screen]')].map((element) => [
      element,
      element.getAttribute('data-screen') ?? '',
    ]),
  );

export class TourDriver {
  private opened: Opened[] = [];
  private token: Token = { cancelled: true };
  private running: Promise<unknown> = Promise.resolve();

  constructor(
    /** Workspace-relative navigation. */
    private readonly navigate: (path: string) => void,
    private readonly timing: TourTiming = TOUR_TIMING,
  ) {}

  /** Bring the app to `step`, from whatever state it is in. Cancels the transition before it. */
  enter(step: TourStep): Promise<TourOutcome> {
    const token = this.begin();
    const before = this.running;
    const run = (async (): Promise<TourOutcome> => {
      await before;
      if (stopped(token)) return 'cancelled';
      return this.arrive(step, token);
    })();
    this.running = run;
    return run;
  }

  /** The tour is over: stop, and close everything it opened. */
  finish(): Promise<void> {
    const token = this.begin();
    const before = this.running;
    const run = (async (): Promise<void> => {
      await before;
      await this.teardown([], token);
    })();
    this.running = run;
    return run;
  }

  /** Stop whatever is running, now. */
  cancel(): void {
    this.token.cancelled = true;
  }

  private begin(): Token {
    this.token.cancelled = true;
    this.token = { cancelled: false };
    return this.token;
  }

  private async arrive(step: TourStep, token: Token): Promise<TourOutcome> {
    const wanted = step.before.flatMap((action) =>
      action.opens === undefined ? [] : [action.opens],
    );
    await this.teardown(wanted, token);
    if (stopped(token)) return 'cancelled';
    if (step.route !== null) {
      await this.page(step.route, token);
      if (stopped(token)) return 'cancelled';
      /* A page change can take a surface with it. */
      this.opened = this.opened.filter((surface) => live(surface.element));
    }
    const ready = await this.perform(step.before, token);
    if (stopped(token)) return 'cancelled';
    return ready ? 'ready' : 'missing';
  }

  /**
   * Close what the next step does not need: the recorded surfaces past the longest run it opens too,
   * top-down. A stray menu or popover goes first, by an outside press.
   */
  private async teardown(wanted: readonly string[], token: Token): Promise<void> {
    if (pageGone()) return;
    this.opened = this.opened.filter((surface) => live(surface.element));

    if (
      [...document.querySelectorAll(DISMISSABLE)].some(
        (element) => element.closest('[data-spotlight]') === null,
      )
    ) {
      document.body.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
      await pause(this.timing.tick);
    }

    let keep = 0;
    while (keep < this.opened.length && this.opened[keep]?.key === wanted[keep]) keep += 1;

    /* ⚠️ Popped only once closed: a teardown cut short by a newer transition leaves the rest
       recorded, for that transition's teardown to close. */
    while (this.opened.length > keep) {
      if (stopped(token) || pageGone()) return;
      const top = this.opened[this.opened.length - 1] as Opened;
      await this.close(top.element);
      this.opened.pop();
    }
  }

  /**
   * One surface, closed. ⚠️ Not cancellable: a half-closed surface is the state nobody can reason
   * about, and this takes one exit animation at most.
   */
  private async close(element: HTMLElement): Promise<void> {
    const started = Date.now();
    while (!pageGone() && live(element) && Date.now() - started < this.timing.surface) {
      escapeBelowSpotlight();
      const sent = Date.now();
      while (!pageGone() && live(element) && Date.now() - sent < this.timing.surface / 4) {
        await pause(this.timing.tick);
      }
    }
    /* ⚠️ One more tick, so the closed surface has let go of the modal stack before the next Escape:
       `inert` lands in the render, the stack pops in the effect after it. */
    await pause(this.timing.tick);
  }

  /** Go to the step's page, unless the app is already on it, and wait for its screen. */
  private async page(route: string, token: Token): Promise<void> {
    const path = route.split('?')[0] ?? route;
    if (pageGone() || window.location.pathname.endsWith(path)) return;
    const before = screens();
    this.navigate(route);

    let started = Date.now();
    /* ⚠️ The cancel is checked BEFORE the address is read, here and in `find`: a tour unmounted
       mid-wait wakes from its pause into a page that may be gone — in a test, a torn-down jsdom
       with no `window` at all, which surfaced as an unhandled rejection after the run. */
    for (;;) {
      if (stopped(token) || pageGone()) return;
      if (window.location.pathname.endsWith(path)) break;
      if (Date.now() - started >= this.timing.route) return;
      await pause(this.timing.tick);
    }
    /* ⚠️ The address changes before the old screen unmounts, and the old screen's table answers the
       new step's selector just as well. A screen that mounted, or changed which screen it is, is
       the new page's. */
    started = Date.now();
    while (Date.now() - started < this.timing.screen) {
      if (stopped(token) || pageGone()) return;
      const fresh = [...screens()].some(([element, name]) => before.get(element) !== name);
      if (fresh) return;
      await pause(this.timing.tick);
    }
  }

  /**
   * The step's clicks, from the first one whose surface is not already open. `false`: a required
   * element never came.
   *
   * ⚠️ **A click is `element.click()`** — the same event React handles for a person, so a tab
   * switches, a row opens and a menu item picks exactly as if somebody had pressed it. The
   * spotlight's blocking layer stops pointers, not these.
   */
  private async perform(actions: readonly TourAction[], token: Token): Promise<boolean> {
    let from = 0;
    let depth = 0;
    for (const [at, action] of actions.entries()) {
      if (action.opens === undefined) continue;
      const open = this.opened[depth];
      if (open?.key !== action.opens || !live(open.element)) break;
      depth += 1;
      from = at + 1;
    }

    for (const action of actions.slice(from)) {
      if (stopped(token) || pageGone()) return false;
      if (action.unless !== undefined && findLive(action.unless) !== null) continue;
      const element =
        action.ifPresent === true
          ? findLive(action.click)
          : await this.find(
              action.click,
              action.required === true ? this.timing.required : this.timing.optional,
              token,
            );
      if (stopped(token) || pageGone()) return false;
      if (element === null) {
        if (action.required === true) return false;
        continue;
      }

      const before = action.opens === undefined ? null : new Set(liveModals());
      element.click();
      if (action.opens !== undefined && before !== null) {
        const surface = await this.appeared(before, token);
        if (surface !== null) this.opened.push({ key: action.opens, element: surface });
      } else {
        /* Long enough for a menu to open before the next click looks for its item. */
        await pause(this.timing.tick);
      }
    }
    return !stopped(token);
  }

  private async find(selector: string, ms: number, token: Token): Promise<HTMLElement | null> {
    const started = Date.now();
    for (;;) {
      if (stopped(token) || pageGone()) return null;
      const found = findLive(selector);
      if (found !== null || Date.now() - started >= ms) return found;
      await pause(this.timing.tick);
    }
  }

  /**
   * The modal surface a click opened. ⚠️ Looked for once more after a cancel, so a surface that did
   * open is recorded — and closed by the next teardown — rather than left behind.
   */
  private async appeared(before: Set<HTMLElement>, token: Token): Promise<HTMLElement | null> {
    const started = Date.now();
    for (;;) {
      if (pageGone()) return null;
      const surface = liveModals().find((modal) => !before.has(modal));
      if (surface !== undefined) return surface;
      if (stopped(token) || Date.now() - started >= this.timing.surface) return null;
      await pause(this.timing.tick);
    }
  }
}

/**
 * What the tour changes that belongs to the person — the Home sections it opens — remembered when it
 * starts. The returned function puts it back.
 */
export function rememberStorage(prefix: string): () => void {
  const saved = new Map<string, string>();
  try {
    for (let at = 0; at < window.localStorage.length; at += 1) {
      const key = window.localStorage.key(at);
      const value = key === null ? null : window.localStorage.getItem(key);
      if (key !== null && value !== null && key.startsWith(prefix)) saved.set(key, value);
    }
  } catch {
    /* ⚠️ Storage can throw outright — a private window. Nothing remembered, nothing to restore. */
    return () => undefined;
  }
  return () => {
    try {
      const now: string[] = [];
      for (let at = 0; at < window.localStorage.length; at += 1) {
        const key = window.localStorage.key(at);
        if (key?.startsWith(prefix) === true) now.push(key);
      }
      for (const key of now) if (!saved.has(key)) window.localStorage.removeItem(key);
      for (const [key, value] of saved) window.localStorage.setItem(key, value);
    } catch {
      /* Restoring is a courtesy; losing it must not cost ending the tour. */
    }
  };
}
